import { useEffect, useRef, useState } from "react";
import { createPortal } from "react-dom";
import { CliInstallContent } from "./CliInstall";

// A small terminal glyph, styled to match the RobotIcon/PersonIcon in the presence pill.
function TerminalIcon() {
  return (
    <svg viewBox="0 0 24 24" width="16" height="16" fill="none" stroke="currentColor" strokeWidth="1.7" strokeLinecap="round" strokeLinejoin="round">
      <rect x="3" y="4" width="18" height="16" rx="2" />
      <path d="M7 9l3 3-3 3" />
      <line x1="13" y1="16" x2="17" y2="16" />
    </svg>
  );
}

// Top-bar pill (next to the presence pill) that opens a popover with the
// omni-hub-cli install instructions. Mirrors TopBar's presence-pill + portal
// popover pattern: the popover renders on <body> so page content can't cover it,
// anchored to the pill's on-screen position captured when opening.
export default function CliInstallButton() {
  const [open, setOpen] = useState(false);
  const pillRef = useRef<HTMLButtonElement>(null);
  const [coords, setCoords] = useState<{ top: number; right: number }>({ top: 64, right: 24 });

  function toggleOpen() {
    if (!open && pillRef.current) {
      const r = pillRef.current.getBoundingClientRect();
      setCoords({ top: r.bottom + 8, right: Math.max(8, window.innerWidth - r.right) });
    }
    setOpen((o) => !o);
  }

  // Close on Escape for keyboard users (backdrop click handles pointer dismissal).
  useEffect(() => {
    if (!open) return;
    const onKey = (e: KeyboardEvent) => { if (e.key === "Escape") setOpen(false); };
    window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
  }, [open]);

  return (
    <div className="presence">
      <button
        ref={pillRef}
        className="presence-pill cli-launch"
        onClick={toggleOpen}
        title="Install the omni-hub-cli on your desktop"
        aria-expanded={open}
      >
        <span className="presence-seg"><TerminalIcon /><span className="cli-launch-label">omni-hub-cli</span></span>
      </button>

      {open && createPortal(
        <>
          <div className="presence-backdrop" onClick={() => setOpen(false)} />
          <div className="cli-pop" role="dialog" aria-label="Install omni-hub-cli" style={{ top: coords.top, right: coords.right }}>
            <CliInstallContent />
          </div>
        </>,
        document.body,
      )}
    </div>
  );
}
